import type { ISchedulerTask, ISiteFloor } from '../../interfaces/IScheduler.js';
import { windowOverlaps } from '../activitiesFilter.js';
import { issueStatusKey } from '../pageFilters.js';
import { daysBetween } from './days.js';
import { freezeOf, isFrozen } from './freeze.js';
import { countDone } from './locationProgress.js';
import { ladderOf, type LadderRung } from './ladder.js';
import type { IListItem } from './listItem.js';
import type { IWindowBounds } from './planWindow.js';

// ---------------------------------------------------------------------------
// A PROMISE is a committed activity — a row of the feed. What every surface
// that lists them says about one (docs/specs/2026-09-22-work-plan-view-design.md):
// the rung it stands on, how late it is, the days it holds, and the facts its
// card carries. The web's Daily List card and Work Plan bar, and the mobile
// list, all read them from here.
// ---------------------------------------------------------------------------

/** The rungs a committed row can stand on — the Status checkboxes' activity
 *  boxes wherever the rows wear the ladder. */
export const PLAN_STATUSES: readonly LadderRung[] = ['committed', 'inProgress', 'delayed', 'overdue', 'areasDone', 'done'];

const later = (a: string, b: string): string => (a > b ? a : b);

/** The rungs whose promise is still owed past its planned end: overdue, and
 *  delayed — Trusstor keeps a promise that never started `delayed` even once
 *  its end has passed, and that work is as late as an overdue one. */
const STRETCHING_RUNGS: ReadonlySet<LadderRung> = new Set(['overdue', 'delayed']);

/** The span a promise occupies: a late one (overdue, or delayed — never
 *  started) stretches to today once its planned end has passed, so a window
 *  holding today still holds it. */
export function openSpan(task: Pick<ISchedulerTask, 'start' | 'end'>, rung: LadderRung, today: string): [string, string] {
  return [task.start, STRETCHING_RUNGS.has(rung) ? later(task.end, today) : task.end];
}

/**
 * How many days late a promise is, by its rung: an overdue one past its
 * planned END (the Work Plan's overrun), a delayed one past its planned START
 * (Trusstor's delayed: no work recorded by then). 0 on any other rung, and on
 * the day itself — Trusstor's clock may call it late before the site's day turns.
 */
export function lateDaysOf(task: Pick<ISchedulerTask, 'start' | 'end'>, rung: LadderRung, today: string): number {
  if (rung === 'overdue') return daysBetween(task.end, later(task.end, today));
  if (rung === 'delayed') return daysBetween(task.start, later(task.start, today));
  return 0;
}

/** A committed activity item's rung (the feed guarantees the item is one) —
 *  what the Daily List's card and the Work Plan's bar both wear. */
export const rungOf = (item: IListItem): LadderRung => ladderOf(item.raw.activity!, { activityStatus: item.activityStatus ?? null }).rung;

/** The Status checkbox a list item answers to: an activity's rung, an
 *  issue's keyed status. */
export function itemStatusKey(item: IListItem): string {
  return item.kind === 'issue' ? issueStatusKey(item.status) : rungOf(item);
}

/** Does a list item belong in the page's date window? An activity when its
 *  span — an overdue one stretched to today — overlaps it. An issue always: it
 *  isn't scheduled work, so its due date never hides it (an open one due last
 *  week stays listed); only the Status checkboxes do — the Work Plan's issue
 *  lane shows every issue the same way. */
export function inWindow(item: IListItem, today: string, window: IWindowBounds): boolean {
  if (item.kind === 'issue') return true;
  return windowOverlaps(...openSpan(item.raw.activity!, rungOf(item), today), [window.from, window.to]);
}

/** Zone id → the catalog index of its floor (the building order). */
export function zoneFloors(floors: readonly ISiteFloor[]): ReadonlyMap<string, number> {
  return new Map(floors.flatMap((floor, index) => floor.zones.map((zone) => [zone.id, index] as const)));
}

/** The catalog indices of the floors a task touches, in building order. */
export function floorsOf(task: ISchedulerTask, floorOfZone: ReadonlyMap<string, number>): number[] {
  const indices = (task.locations ?? [])
    .map((location) => floorOfZone.get(location.locationId))
    .filter((index): index is number => index !== undefined);
  return [...new Set(indices)].sort((a, b) => a - b);
}

/** What a card says about a promise, like a Trusstor calendar card: where it
 *  is, how many of its areas are done, and the work time the IoT counted. */
export interface IPromiseFacts {
  /** The first floor it touches (building order); null when none is known. */
  floor: string | null;
  /** How many further floors it spans. */
  moreFloors: number;
  areasDone: number;
  areasTotal: number;
  /** Tag work time across its areas, in hours to one decimal; 0 when none. */
  workHours: number;
}

export function promiseFacts(task: ISchedulerTask, floors: readonly ISiteFloor[], floorOfZone: ReadonlyMap<string, number>): IPromiseFacts {
  const locations = task.locations ?? [];
  const touched = floorsOf(task, floorOfZone);
  const workMs = locations.reduce((sum, location) => sum + (location.workTimeMs ?? 0), 0);
  return {
    floor: touched.length > 0 ? floors[touched[0]!]!.name : null,
    moreFloors: Math.max(0, touched.length - 1),
    areasDone: countDone(locations),
    areasTotal: locations.length,
    workHours: Math.round(workMs / 360_000) / 10,
  };
}

/** A phrase a surface prints: an i18n key and its interpolation. */
export interface IPromisePhrase {
  key: string;
  params?: Record<string, number>;
}

/** The parts of a card line, joined by a middle dot; empty ones dropped. */
export const joinParts = (parts: readonly (string | null | undefined)[]): string => parts.filter(Boolean).join(' · ');

/** A card's WHERE: the first floor, with how many more it spans; null when
 *  no floor is known. */
export function promiseFloorLabel(facts: IPromiseFacts): string | null {
  if (!facts.floor) return null;
  return facts.moreFloors > 0 ? `${facts.floor} +${facts.moreFloors}` : facts.floor;
}

/** A card's HOW FAR, after the bar: the areas done of all, and the work time
 *  the IoT counted — each only when there is something to count. */
export function promiseMeasureParts(facts: IPromiseFacts): IPromisePhrase[] {
  return [
    ...(facts.areasTotal > 0 ? [{ key: 'activities.card.areas', params: { done: facts.areasDone, total: facts.areasTotal } }] : []),
    ...(facts.workHours > 0 ? [{ key: 'activities.card.workHours', params: { hours: facts.workHours } }] : []),
  ];
}

// ---------------------------------------------------------------------------
// The promise as it stands, in the rows the commit review asks for
// (2026-09-18 commit UX design, "Drawer" -> Promise block): the web drawer's
// Promise block and the mobile task sheet both render these. Each row is
// filled (ok), still needed (need), or an optional fact (info).
// ---------------------------------------------------------------------------

export type PromiseRowName = 'kind' | 'profession' | 'areas' | 'assignee' | 'safety';
export type PromiseRowState = 'ok' | 'need' | 'info';

export interface IPromiseReviewRow {
  row: PromiseRowName;
  state: PromiseRowState;
  /** The value when it is a fixed phrase: the kind, an unnamed assignee, the
   *  safety flag. */
  valueKey?: string;
  /** The value when it is free text: the assignee's name. The trade and the
   *  areas are the task's own data, which each surface draws its own way; a
   *  `need` row has no value, and each surface words its gap its own way. */
  value?: string;
  /** The lines under the value. */
  notes: IPromisePhrase[];
}

/**
 * Why a promise's areas are no longer a plan edit, as its note's i18n key:
 * frozen once the field is seen working, else fixed by the commit (they became
 * Trusstor area sequence items, 2026-09-23 amendment); null while still open.
 */
export function areasLockKey(task: Pick<ISchedulerTask, 'committed'>, rung: LadderRung): string | null {
  if (isFrozen(freezeOf(rung), 'areas')) return 'scheduler.freeze.scope';
  return task.committed ? 'scheduler.freeze.committed' : null;
}

/**
 * The promise's rows, in order. Administrative work has no Trusstor side — no
 * trade, no areas, no safety flag — so it shows its kind instead. Assignee and
 * Safety are answered IN the commit review and stored on the activity, which
 * only exists once committed, so they join the rows after the commit: before
 * it, "Unassigned" / "Not flagged" would read as gaps nobody can fill.
 */
export function promiseReviewRows(task: ISchedulerTask, rung: LadderRung): IPromiseReviewRow[] {
  const isAdmin = task.kind === 'administrative';
  const rows: IPromiseReviewRow[] = isAdmin
    ? [{ row: 'kind', state: 'info', valueKey: 'scheduler.commit.kind.administrative', notes: [] }]
    : [{ row: 'profession', state: task.profession ? 'ok' : 'need', notes: [] }, areasRow(task, rung)];
  if (!task.committed) return rows;
  rows.push(
    task.assignee
      ? { row: 'assignee', state: 'ok', value: task.assignee, notes: [] }
      : { row: 'assignee', state: 'info', valueKey: 'scheduler.commit.unassigned', notes: [] },
  );
  if (!isAdmin) {
    rows.push({ row: 'safety', state: 'info', valueKey: task.dangerousActivity ? 'scheduler.commit.dangerous' : 'scheduler.commit.notFlagged', notes: [] });
  }
  return rows;
}

/** Once the field is reporting, the areas row carries how far it has got; once
 *  they are locked, why. */
function areasRow(task: ISchedulerTask, rung: LadderRung): IPromiseReviewRow {
  const locations = task.locations ?? [];
  const done = countDone(locations);
  const lock = areasLockKey(task, rung);
  return {
    row: 'areas',
    state: locations.length > 0 ? 'ok' : 'need',
    notes: [
      ...(done > 0 ? [{ key: 'scheduler.panel.locations.doneCount', params: { done, total: locations.length } }] : []),
      ...(lock ? [{ key: lock }] : []),
    ],
  };
}
